import React from 'react';
import { TrendUp, Target, ArrowsLeftRight, ChartBar, Trophy, Fire, Eye } from 'phosphor-react';
import { useLanguage } from '../context/LanguageContext';

// GENI Brain logo fallback for avatars
const GENI_BRAIN_FALLBACK = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/gtg4vc6a_purebrain.png";

/**
 * ProfileSummaryHeader
 * 
 * Shows performance metrics for a trading profile.
 * 
 * For PUBLIC profiles:
 * - Shows profile avatar (or GENI brain fallback)
 * - Shows "Trading Performance" title
 * - Shows "[name]'s verified trading journal" subtitle
 * - Shows "Public Profile • Read only" indicator
 * - Removes duplicate Profit Factor display
 * - Hides Avg RR if it's 0.00 (no data)
 * 
 * Props:
 * - analytics: object with total_trades, win_rate, avg_rr, profit_factor, public_profile_avatar_url
 * - username: string - profile display name
 * - isPublicProfile: boolean - if true, use public profile layout
 * - avatarUrl: string - custom avatar URL (optional, can also be in analytics)
 */
export default function ProfileSummaryHeader({ analytics, username, isPublicProfile = false, avatarUrl }) {
  const { t, language } = useLanguage();

  if (!analytics) return null;

  const totalTrades = analytics.total_trades || 0;
  const winRate = analytics.win_rate || 0;
  const profitFactor = analytics.profit_factor || 0;
  const totalR = analytics.total_rr ?? analytics.total_r ?? null;
  const averageRPerMonth = analytics.average_r_per_month;
  const firstTradeAt = analytics.first_trade_at;

  const formatSinceDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;

    return new Intl.DateTimeFormat(language === 'ptBR' ? 'pt-BR' : 'en-US', {
      day: language === 'ptBR' ? '2-digit' : 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(parsed);
  };

  const sinceDateLabel = formatSinceDate(firstTradeAt);

  const formatTotalR = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return 'N/A';
    const numericValue = Number(value);
    if (numericValue === 0) return '0R';
    return `${numericValue > 0 ? '+' : ''}${numericValue.toFixed(2)}R`;
  };

  const getTotalRColor = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return 'text-white/60';
    const numericValue = Number(value);
    if (numericValue > 0) return 'text-[#00E676]';
    if (numericValue < 0) return 'text-[#FF3B30]';
    return 'text-white';
  };

  const formatAverageRPerMonth = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
    const numericValue = Number(value);
    if (numericValue === 0) return `≈ 0${t('analytics.rPerMonth')}`;
    return `≈ ${numericValue > 0 ? '+' : ''}${numericValue.toFixed(2)}${t('analytics.rPerMonth')}`;
  };

  const averageRPerMonthLabel = formatAverageRPerMonth(averageRPerMonth);

  // Determine skill tier based on profit factor
  const getSkillTier = () => {
    if (profitFactor >= 2.0) return { label: 'Elite', color: 'text-yellow-400', icon: Trophy };
    if (profitFactor >= 1.5) return { label: 'Skilled', color: 'text-[#00E676]', icon: Fire };
    if (profitFactor >= 1.0) return { label: 'Developing', color: 'text-cyan-400', icon: TrendUp };
    return { label: 'Learning', color: 'text-white/60', icon: ChartBar };
  };

  const skillTier = getSkillTier();
  const SkillIcon = skillTier.icon;

  // Color helpers
  const getWinRateColor = (rate) => {
    if (rate >= 55) return 'text-[#00E676]';
    if (rate >= 45) return 'text-[#FFC107]';
    return 'text-[#FF3B30]';
  };

  const getProfitFactorColor = (pf) => {
    if (pf >= 1.5) return 'text-[#00E676]';
    if (pf >= 1.0) return 'text-[#FFC107]';
    return 'text-[#FF3B30]';
  };

  // Get display name for public profile
  const displayName = username || 'Trader';

  // Get avatar URL from prop or analytics
  const profileAvatarUrl = avatarUrl || analytics?.public_profile_avatar_url || null;

  // PUBLIC PROFILE LAYOUT
  if (isPublicProfile) {
    return (
      <div className="mb-8" data-testid="profile-summary-header">
        {/* Header Section with Avatar */}
        <div className="mb-6">
          {/* Public Profile Indicator - Discrete */}
          <div className="flex items-center gap-2 mb-4">
            <Eye size={14} weight="bold" className="text-white/40" />
            <span className="text-white/40 text-xs font-medium uppercase tracking-wider">
              Public Profile • Read only
            </span>
          </div>
          
          {/* Avatar + Title Row */}
          <div className="flex items-center gap-5">
            {/* Profile Avatar */}
            <div 
              className="w-20 h-20 rounded-full overflow-hidden border-2 border-white/20 bg-[#121212] flex-shrink-0 shadow-lg"
              data-testid="public-profile-avatar"
            >
              <img
                src={profileAvatarUrl || GENI_BRAIN_FALLBACK}
                alt={`${displayName}'s avatar`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = GENI_BRAIN_FALLBACK;
                }}
              />
            </div>
            
            {/* Title + Subtitle */}
            <div>
              <h1 className="text-3xl md:text-4xl font-black font-heading tracking-tight mb-1">
                Trading Performance
              </h1>
              <p className="text-white/60 text-base">
                {displayName}&apos;s verified trading journal
              </p>
              {sinceDateLabel ? (
                <p className="mt-2 text-xs font-mono tracking-[0.04em] text-white/42" data-testid="public-profile-since-label">
                  {t('common.since')} {sinceDateLabel}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        {/* Skill Badge + Single Metrics Row */}
        <div className="bg-gradient-to-br from-[#1a1a1a] to-[#121212] border border-white/10 rounded-lg p-6 shadow-xl">
          {/* Skill Badge - Left aligned, no duplicate counts */}
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-white/5">
            <div className={`p-2 rounded-full bg-white/5 ${skillTier.color}`}>
              <SkillIcon size={24} weight="fill" />
            </div>
            <div className={`text-lg font-bold ${skillTier.color}`}>
              {skillTier.label} Trader
            </div>
          </div>

          {/* Metrics Row - NO DUPLICATES */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="public-profile-overview-metrics">
            <div className="text-center p-3 bg-white/5 rounded-sm" data-testid="public-profile-overview-total-trades-card">
              <div className="text-white/40 text-[10px] font-mono uppercase mb-1">Total Trades</div>
              <div className="text-xl font-bold text-white font-mono" data-testid="public-profile-overview-total-trades-value">{totalTrades}</div>
            </div>
            <div className="text-center p-3 bg-white/5 rounded-sm" data-testid="public-profile-overview-win-rate-card">
              <div className="text-white/40 text-[10px] font-mono uppercase mb-1">Win Rate</div>
              <div className={`text-xl font-bold font-mono ${getWinRateColor(winRate)}`} data-testid="public-profile-overview-win-rate-value">
                {winRate.toFixed(1)}%
              </div>
            </div>
            <div className="text-center p-3 bg-white/5 rounded-sm" data-testid="public-profile-overview-total-r-card">
              <div className="text-white/40 text-[10px] font-mono uppercase mb-1">Total R</div>
              <div className={`text-xl font-bold font-mono ${getTotalRColor(totalR)}`} data-testid="public-profile-overview-total-r-value">{formatTotalR(totalR)}</div>
              {averageRPerMonthLabel ? (
                <div className="mt-1 text-[11px] text-white/42 font-mono" data-testid="public-profile-overview-total-r-monthly-value">{averageRPerMonthLabel}</div>
              ) : null}
            </div>
            <div className="text-center p-3 bg-white/5 rounded-sm" data-testid="public-profile-overview-profit-factor-card">
              <div className="text-white/40 text-[10px] font-mono uppercase mb-1">Profit Factor</div>
              <div className={`text-xl font-bold font-mono ${getProfitFactorColor(profitFactor)}`} data-testid="public-profile-overview-profit-factor-value">
                {profitFactor.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // PRIVATE DASHBOARD LAYOUT (original)
  return (
    <div className="bg-gradient-to-br from-[#1a1a1a] to-[#121212] border border-white/10 rounded-lg p-6 mb-8 shadow-xl" data-testid="profile-summary-header">
      {/* Skill Badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full bg-white/5 ${skillTier.color}`}>
            <SkillIcon size={24} weight="fill" />
          </div>
          <div>
            <div className={`text-sm font-bold ${skillTier.color}`}>{skillTier.label} Trader</div>
            <div className="text-white/40 text-xs">{totalTrades} completed trades</div>
            {sinceDateLabel ? (
              <div className="mt-1 text-white/32 text-[11px] font-mono tracking-[0.04em]" data-testid="shared-profile-since-label">
                {t('common.since')} {sinceDateLabel}
              </div>
            ) : null}
          </div>
        </div>
        <div className="text-right">
          <div className="text-white/40 text-xs font-mono">PROFIT FACTOR</div>
          <div className={`text-2xl font-bold font-mono ${getProfitFactorColor(profitFactor)}`}>
            {profitFactor.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="shared-profile-overview-metrics">
        <div className="text-center p-3 bg-white/5 rounded-sm" data-testid="shared-profile-overview-total-trades-card">
          <div className="text-white/40 text-[10px] font-mono uppercase mb-1">Total Trades</div>
          <div className="text-xl font-bold text-white font-mono" data-testid="shared-profile-overview-total-trades-value">{totalTrades}</div>
        </div>
        <div className="text-center p-3 bg-white/5 rounded-sm" data-testid="shared-profile-overview-win-rate-card">
          <div className="text-white/40 text-[10px] font-mono uppercase mb-1">Win Rate</div>
          <div className={`text-xl font-bold font-mono ${getWinRateColor(winRate)}`} data-testid="shared-profile-overview-win-rate-value">
            {winRate.toFixed(1)}%
          </div>
        </div>
        <div className="text-center p-3 bg-white/5 rounded-sm" data-testid="shared-profile-overview-total-r-card">
          <div className="text-white/40 text-[10px] font-mono uppercase mb-1">Total R</div>
          <div className={`text-xl font-bold font-mono ${getTotalRColor(totalR)}`} data-testid="shared-profile-overview-total-r-value">{formatTotalR(totalR)}</div>
          {averageRPerMonthLabel ? (
            <div className="mt-1 text-[11px] text-white/42 font-mono" data-testid="shared-profile-overview-total-r-monthly-value">{averageRPerMonthLabel}</div>
          ) : null}
        </div>
        <div className="text-center p-3 bg-white/5 rounded-sm" data-testid="shared-profile-overview-profit-factor-card">
          <div className="text-white/40 text-[10px] font-mono uppercase mb-1">Profit Factor</div>
          <div className={`text-xl font-bold font-mono ${getProfitFactorColor(profitFactor)}`} data-testid="shared-profile-overview-profit-factor-value">
            {profitFactor.toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
}
